import Console from '~/utils/lib/core/console'
import { createDemandThresholdController } from '~/utils/infra/common/ws/demand-threshold-controller'

/**
 * @typedef {{
 *   source: string;
 *   client: {
 *     connect: (options?: object) => Promise<void>;
 *     disconnect: () => Promise<void>;
 *   };
 *   onAcquire?: (payload: object) => Promise<void>;
 *   onRelease?: (payload: object) => Promise<void>;
 *   isConfigured?: () => boolean;
 *   getConnectOptions?: () => object;
 *   socketObserver?: { on: (subject: string, fn: Function) => string };
 *   socketEvents?: {
 *     ON_SOCKET_OPEN?: string;
 *     ON_SOCKET_CLOSE?: string;
 *     ON_SOCKET_ERROR?: string;
 *   };
 *   thresholdOptions?: object;
 * }} RuntimeOptions
 */

/**
 * @param {RuntimeOptions} options
 */
export function createDemandDrivenRuntime(options) {
  const console = new Console(`plugin:realtime:${options.source}`)
  /** @type {Map<string, { payload: object, count: number }>} */
  const demandEntries = new Map()

  const demandController = createDemandThresholdController({
    connect: connect,
    disconnect: disconnect,
    options: options.thresholdOptions,
  })

  bindSocketState()

  /**
   * @param {object} payload
   */
  function acquireDemand(payload) {
    if (!isObjectPayload(payload))
      return Promise.reject(new Error(`invalid demand payload for ${options.source}`))

    const key = getDemandKey(payload)
    const current = demandEntries.get(key)
    const isFirstDemand = !current

    if (current) {
      current.count += 1
    } else {
      demandEntries.set(key, { payload, count: 1 })
    }

    return demandController.acquire()
      .then(() => {
        if (!isFirstDemand)
          return Promise.resolve()

        return notifyAcquire(payload)
          .catch((error) => {
            demandController.recordError()
            console.warn('acquire demand failed', error)
          })
      })
      .then(() => {
        let demandReleased = false
        return {
          release() {
            if (demandReleased)
              return Promise.resolve()

            demandReleased = true
            return releaseDemand(payload)
          }
        }
      })
      .catch((error) => {
        rollbackAcquireDemand({ key, current, isFirstDemand })
        return demandController.release()
          .catch((releaseError) => {
            demandController.recordError()
            console.warn('release after failed acquire demand failed', releaseError)
          })
          .then(() => {
            throw error
          })
      })
  }

  /**
   * @param {object} payload
   */
  function releaseDemand(payload) {
    const key = getDemandKey(payload)
    const current = demandEntries.get(key)

    if (!current)
      return Promise.resolve()

    current.count -= 1

    if (current.count <= 0) {
      demandEntries.delete(key)
      notifyRelease(current.payload)
        .catch((error) => {
          demandController.recordError()
          console.warn('release demand failed', error)
        })
    }

    return demandController.release()
  }

  function forceDisconnect({ clearSubscriptions = false } = {}) {
    if (clearSubscriptions) {
      demandEntries.forEach((entry) => {
        notifyRelease(entry.payload)
          .catch((error) => {
            demandController.recordError()
            console.warn('release demand on reset failed', error)
          })
      })
      demandEntries.clear()
    }

    return demandController.forceDisconnect()
  }

  function getSnapshot() {
    return {
      source: options.source,
      demandEntryCount: demandEntries.size,
      demand: demandController.getSnapshot(),
    }
  }

  function connect() {
    if (options.isConfigured && !options.isConfigured())
      return Promise.resolve(false)

    const connectOptions = options.getConnectOptions
      ? options.getConnectOptions()
      : undefined

    return options.client.connect(connectOptions)
      .then(() => true)
      .catch((error) => {
        demandController.recordError()
        console.warn('connect failed', error)
        throw error
      })
  }

  function disconnect() {
    return options.client.disconnect()
      .then(() => {
        demandController.markDisconnected()
      })
      .catch((error) => {
        demandController.recordError()
        console.warn('disconnect failed', error)
      })
  }

  /**
   * @param {object} payload
   * @returns {Promise<void>}
   */
  function notifyAcquire(payload) {
    if (!options.onAcquire)
      return Promise.resolve()

    return options.onAcquire(payload)
  }

  /**
   * @param {object} payload
   * @returns {Promise<void>}
   */
  function notifyRelease(payload) {
    if (!options.onRelease)
      return Promise.resolve()

    return options.onRelease(payload)
  }

  /**
   * @param {{
   *   key: string;
   *   current: { payload: object, count: number } | undefined;
   *   isFirstDemand: boolean;
   * }} params
   */
  function rollbackAcquireDemand(params) {
    if (params.isFirstDemand) {
      demandEntries.delete(params.key)
      return
    }

    if (!params.current)
      return

    params.current.count -= 1
    if (params.current.count <= 0)
      demandEntries.delete(params.key)
  }

  function bindSocketState() {
    if (!options.socketObserver || !options.socketEvents)
      return

    if (options.socketEvents.ON_SOCKET_OPEN) {
      options.socketObserver.on(
        options.socketEvents.ON_SOCKET_OPEN,
        () => demandController.markConnected()
      )
    }

    if (options.socketEvents.ON_SOCKET_CLOSE) {
      options.socketObserver.on(
        options.socketEvents.ON_SOCKET_CLOSE,
        () => demandController.markDisconnected()
      )
    }

    if (options.socketEvents.ON_SOCKET_ERROR) {
      options.socketObserver.on(
        options.socketEvents.ON_SOCKET_ERROR,
        () => demandController.recordError()
      )
    }
  }

  return {
    acquireDemand,
    releaseDemand,
    forceDisconnect,
    getSnapshot,
  }
}

/**
 * @param {object} payload
 * @returns {string}
 */
function getDemandKey(payload) {
  return JSON.stringify(payload)
}

/**
 * @param {any} payload
 * @returns {boolean}
 */
function isObjectPayload(payload) {
  return typeof payload === 'object'
    && payload !== null
    && !Array.isArray(payload)
}
